
  import { defineComponent, ref, inject, watch, computed } from 'vue';
  import { ASSIGNEES_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
  import { canAddUser, hasPermission, Permissions, canSelfServeSubscription } from '@/ui/auth/auth';
  import { SelectOptions } from '@/ui/types';
  import { ValidationError } from '@/entities/validation';
  import { Icon } from '@/ui/components/icon/Icon';
  import { TagInput } from '@/ui/components/controls/TagInput';
  import { InviteByEmailModal } from '@/ui/pages/user/assignees/InviteByEmailModal';
  import { CreateUserModal, CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { AddUserSeatsModal } from '@/ui/components/modals/AddUserSeatsModal';
  import CannotAddUserSeatsModal from '@/ui/components/modals/CannotAddUserSeatsModal.vue';
  import { createUserIdentifierFromActionPageKey } from '@/ui/injectionKeys';
  import { useModal } from '@/ui/concerns/modals/useModal';

  type AssigneesType = Array<number | string>;

  interface AssigneesDropdownProps {
    value: AssigneesType;
    options: SelectOptions;
    errors: ValidationError[];
    disabled?: boolean;
    disabledExternalAssignee?: boolean;
    hideExternalAssignee?: boolean;
    nonVisibleOptions?: SelectOptions;
    placeholder?: string;
  }

  export default defineComponent<AssigneesDropdownProps>({
    name: 'AssigneesDropdown',
    components: {
      Icon,
      TagInput,
      InviteByEmailModal,
      CreateUserModal,
      AddUserSeatsModal,
      CannotAddUserSeatsModal,
    },
    props: {
      value: { type: Array as () => AssigneesType, required: true },
      options: { type: Array as () => SelectOptions, required: true },
      errors: { type: Array as () => ValidationError[], required: true },
      disabled: { type: Boolean, default: false },
      disabledExternalAssignee: { type: Boolean, default: false },
      hideExternalAssignee: { type: Boolean, default: false },
      nonVisibleOptions: { type: Array as () => SelectOptions, default: () => [] },
      placeholder: { type: String, default: '' },
    },
    setup(props, { emit }) {
      const identifier = inject<CreateUserModelIdentifier>(createUserIdentifierFromActionPageKey);

      const selected = ref<AssigneesType>(props.value);

      const [showModal, openInviteByEmailModal, closeInviteByEmailModal] = useModal();
      const [isAddUserModalOpen, openAddUserModal, closeAddUserModal] = useModal();
      const [isAddUserSeatModalOpen, openAddUserSeatModal, closeAddUserSeatModal] = useModal();

      const disabledExternalAssigneeComputed = computed(() => !!props.disabledExternalAssignee);
      const hideExternalAssigneeComputed = computed(() => !!props.hideExternalAssignee);

      watch(() => props.value, val => {
        selected.value = val;
      });

      const onUserCreated = (value: number|string) => {
        closeAddUserModal();
        emit('user-created', value);
      };

      const onCreateUserClick = () => {
        if (canAddUser()) {
          openAddUserModal();

          return;
        }

        openAddUserSeatModal();
      };

      const onUserSeatAdded = () => {
        closeAddUserSeatModal();
        openAddUserModal();
      };

      const onInviteByEmail = (email: string) => {
        selected.value.push(email);
        emit('input', selected.value);
      };

      const onAssignByEmail = () => {
        if (disabledExternalAssigneeComputed.value) {
          return;
        }
        // close dropdown
        document.querySelector('body')?.click();
        emit('blur');
        openInviteByEmailModal();
      };

      const emitChange = (value: AssigneesType) => {
        emit('input', value);
      };

      const assigneesLimit = ASSIGNEES_ITEMS_DROPDOWN_LIMIT;

      return {
        assigneesLimit,
        Permissions,
        identifier,
        selected,
        showModal,
        isAddUserModalOpen,
        isAddUserSeatModalOpen,
        disabledExternalAssigneeComputed,
        hideExternalAssigneeComputed,
        hasPermission,
        canSelfServeSubscription,
        closeAddUserModal,
        closeAddUserSeatModal,
        closeInviteByEmailModal,
        onUserCreated,
        onCreateUserClick,
        onUserSeatAdded,
        onInviteByEmail,
        onAssignByEmail,
        emitChange,
      };
    },
  });
